import React from 'react';
import { AlertTriangle, Server, Clock } from 'lucide-react';

const AlertsGrid = ({ alerts, cardClasses, isDarkMode }) => {
  const getCloudColor = (nube) => {
    switch (nube) {
      case 'aws': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'azure': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'gcp': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'huawei': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getSeverityColor = (cantidad) => {
    if (cantidad >= 20) return 'bg-red-600 text-white';
    if (cantidad >= 10) return 'bg-red-500 text-white';
    if (cantidad >= 5) return 'bg-yellow-500 text-white';
    return 'bg-blue-500 text-white';
  };

  const getSeverityLabel = (cantidad) => {
    if (cantidad >= 20) return 'Crítico';
    if (cantidad >= 10) return 'Alto';
    if (cantidad >= 5) return 'Medio';
    return 'Bajo';
  };

  const formatTimeRange = (horas) => {
    if (!horas || horas.length <= 1) return horas?.[0] || '';
    
    const sortedHours = [...horas].sort();
    const firstHour = sortedHours[0];
    const lastHour = sortedHours[sortedHours.length - 1];
    
    if (firstHour === lastHour) return firstHour;
    return `${firstHour} - ${lastHour}`;
  };

  const getAlertIcon = (alerta) => {
    const alertLower = alerta.toLowerCase();
    if (alertLower.includes('cpu') || alertLower.includes('high')) return '🔥';
    if (alertLower.includes('disk') || alertLower.includes('space')) return '💾';
    if (alertLower.includes('memory') || alertLower.includes('ram')) return '🧠';
    if (alertLower.includes('network') || alertLower.includes('timeout')) return '🌐';
    if (alertLower.includes('database') || alertLower.includes('sql')) return '🗃️';
    return '⚠️';
  };

  return (
    <div className={`${cardClasses} p-6 rounded-2xl mb-8`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold flex items-center">
          <AlertTriangle className="w-6 h-6 mr-2 text-red-400" />
          Alertas de Hoy ({alerts.length})
        </h3>
        <div className="flex items-center text-sm opacity-70">
          <Clock className="w-4 h-4 mr-1" />
          Agrupadas por tipo
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {alerts.map((alert, index) => (
          <div key={index} className={`${isDarkMode ? 'bg-red-500/10' : 'bg-red-50'} border border-red-500/20 p-4 rounded-xl hover:scale-105 transition-all duration-200 hover:shadow-lg`}>
            {/* Header con nube y severidad */}
            <div className="flex items-center justify-between mb-3">
              <span className={`px-2 py-1 rounded-md text-xs font-semibold border ${getCloudColor(alert.Nube)}`}>
                {alert.Nube}
              </span>
              <div className="flex items-center space-x-1">
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${getSeverityColor(alert.Cantidad)}`}>
                  {alert.Cantidad}
                </span>
                <span className="text-xs opacity-60">{getSeverityLabel(alert.Cantidad)}</span>
              </div>
            </div>
            
            {/* Título de la alerta con emoji */}
            <div className="font-medium mb-2 text-sm leading-tight flex items-start">
              <span className="mr-2 text-lg">{getAlertIcon(alert.Alerta)}</span>
              <span>{alert.Alerta}</span>
            </div>
            
            {/* Footer con tiempo */}
            <div className="flex items-center justify-between text-xs">
              <span className="opacity-70">
                {new Date(alert.Fecha + "T00:00:00").toLocaleDateString("es-MX", { 
                  month: "short", 
                  day: "numeric" 
                })}
              </span>
              <span className="font-mono bg-gray-500/20 px-2 py-1 rounded">
                {formatTimeRange(alert.horas)}
              </span>
            </div>

            {/* Indicador de múltiples ocurrencias */}
            {alert.horas && alert.horas.length > 1 && (
              <div className="mt-2 text-xs opacity-60 text-center">
                <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full">
                  {alert.horas.length} ocurrencias
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {alerts.length === 0 && (
        <div className="text-center py-12 opacity-60">
          <Server className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium mb-2">No hay alertas hoy</p>
          <p className="text-sm">¡Todo funcionando correctamente! 🎉</p>
        </div>
      )}

      {/* Resumen estadístico */}
      {alerts.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-300/20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-red-400">
                {alerts.reduce((sum, alert) => sum + alert.Cantidad, 0)}
              </div>
              <div className="text-xs opacity-70">Total Eventos</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-400">
                {alerts.filter(alert => alert.Cantidad >= 10).length}
              </div>
              <div className="text-xs opacity-70">Alta Severidad</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">
                {[...new Set(alerts.map(alert => alert.Nube))].length}
              </div>
              <div className="text-xs opacity-70">Nubes Afectadas</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-400">
                {alerts.length}
              </div>
              <div className="text-xs opacity-70">Tipos de Alerta</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsGrid;

